import React, { ReactNode } from 'react';
import CreatableSelect from 'react-select/creatable';
import { MultiValue, SingleValue, ActionMeta } from 'react-select';
import { FormatOptionLabelMeta } from 'react-select/dist/declarations/src/Select';

interface Option {
    value: string;
    label: string;
    photoURL?: string;
}

interface MultiSelectProps {
    options: Option[];
    value: string | string[];
    onChange: (selected: string | string[]) => void;
    onCreateOption?: (inputValue: string) => void;
    placeholder: string;
    formatOptionLabel?: (data: Option, formatOptionLabelMeta: FormatOptionLabelMeta<Option>) => ReactNode;
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    name?: string;
    multiple?: boolean;
    onInputChange?: any;
    borderless?: boolean;
    menuOverlay?: boolean;
    searchable?: boolean;
    creatable?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    value,
    onChange,
    onCreateOption,
    placeholder,
    label,
    name,
    formatOptionLabel,
    loading = false,
    disabled = false,
    multiple = true,
    onInputChange,
    borderless = false,
    menuOverlay = false,
    searchable = true,
    creatable = true,
}) => {
    // When `menuOverlay` is enabled we portal the menu to <body> and use `position: fixed`.
    // Our modal/drawer overlays sit at z-[1000], so the menu must be higher than that,
    // otherwise it renders underneath the backdrop and becomes non-interactive (no hover/click).
    const overlayMenuZIndex = 1100;
    const handleChange = (
        newValue: MultiValue<Option> | SingleValue<Option>,
        _actionMeta: ActionMeta<Option>
    ) => {
        if (multiple) {
            const selectedValues = (newValue as MultiValue<Option>).map((option) => option.value);
            onChange(selectedValues);
        } else {
            onChange((newValue as SingleValue<Option>)?.value || '');
        }
    };

    const selectedOptions = multiple
        ? options.filter((option) => (value as string[]).includes(option.value))
        : options.find((option) => option.value === value);

    const menuPortalTarget = menuOverlay && typeof window !== 'undefined' ? document.body : undefined;

    const customStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            borderColor: borderless
                ? 'transparent'
                : state.isFocused
                  ? 'rgb(var(--sf-accent-main-rgb) / 0.34)'
                  : 'var(--sf-control-border)',
            borderRadius: '1rem',
            borderWidth: borderless ? 0 : provided.borderWidth,
            backgroundColor: borderless ? 'transparent' : 'var(--sf-control-bg)',
            color: 'oklch(var(--bc))',
            '&:hover': {
                borderColor: borderless
                    ? 'transparent'
                    : state.isFocused
                      ? 'rgb(var(--sf-accent-main-rgb) / 0.34)'
                      : 'var(--sf-panel-border-strong)',
            },
            minHeight: 46,
            boxShadow: state.isFocused
                ? '0 0 0 4px rgb(var(--sf-accent-main-rgb) / 0.12)'
                : 'inset 0 1px 0 rgb(255 255 255 / 0.06)',
            transition: 'border-color 160ms ease, box-shadow 160ms ease',
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'oklch(var(--bc))',
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            color: 'oklch(var(--bc))',
            flexWrap: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'oklch(var(--bc))',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }),
        menu: (provided: any) => ({
            ...provided,
            position: menuOverlay ? provided.position : 'relative',
            top: menuOverlay ? provided.top : 'auto',
            left: menuOverlay ? provided.left : 'auto',
            marginTop: 8,
            backgroundColor: 'var(--sf-overlay-surface)',
            borderRadius: '1rem',
            border: '1px solid var(--sf-overlay-border)',
            boxShadow: 'var(--sf-overlay-shadow)',
            overflow: 'hidden',
            zIndex: menuOverlay ? overlayMenuZIndex : provided.zIndex,
        }),
        menuList: (provided: any) => ({
            ...provided,
            maxHeight: 260,
            overflowY: 'auto',
            backgroundColor: 'transparent',
            padding: 6,
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            color: state.isDisabled ? 'oklch(var(--bc) / 0.4)' : 'oklch(var(--bc))',
            backgroundColor: state.isSelected
                ? 'rgb(var(--sf-accent-main-rgb) / 0.14)'
                : state.isFocused
                  ? 'rgb(var(--sf-accent-main-rgb) / 0.08)'
                  : 'transparent',
            borderRadius: '0.85rem',
            padding: 0,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        }),
        multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: 'rgb(var(--sf-accent-main-rgb) / 0.1)',
            borderRadius: '9999px',
            paddingLeft: 4,
        }),
        multiValueLabel: (provided: any) => ({ ...provided, color: 'oklch(var(--bc))', fontWeight: 600 }),
        multiValueRemove: (provided: any) => ({
            ...provided,
            color: 'oklch(var(--bc) / 0.7)',
            borderRadius: '9999px',
            '&:hover': { backgroundColor: 'rgb(var(--sf-accent-main-rgb) / 0.14)', color: 'oklch(var(--bc))' },
        }),
        placeholder: (provided: any) => ({ ...provided, color: 'oklch(var(--bc) / 0.5)' }),
        clearIndicator: (base: any) => ({ ...base, padding: '0 10px 0 0' }),
        menuPortal: (provided: any) => ({
            ...provided,
            zIndex: menuOverlay ? overlayMenuZIndex : provided.zIndex,
        }),
    };

    return (
        <div className="form-control">
            {!!label && (
                <div className="label">
                    <span className="label-text">{label}</span>
                </div>
            )}

            <CreatableSelect
                isMulti={multiple}
                options={options}
                value={selectedOptions as any}
                onChange={handleChange}
                onCreateOption={onCreateOption}
                isValidNewOption={creatable ? undefined : () => false}
                placeholder={placeholder}
                isDisabled={disabled}
                isClearable={true}
                formatCreateLabel={(newValue) => (
                    <>
                        Adicionar <b>{newValue}</b>
                    </>
                )}
                formatOptionLabel={formatOptionLabel}
                styles={customStyles}
                isLoading={loading}
                className="basic-multi-select"
                classNamePrefix="select"
                name={name}
                noOptionsMessage={() => 'Nenhuma opção disponível'}
                onInputChange={onInputChange}
                menuShouldBlockScroll={false}
                menuPlacement="auto"
                menuPosition={menuOverlay ? 'fixed' : 'absolute'}
                menuPortalTarget={menuPortalTarget}
                isSearchable={searchable}
            />
        </div>
    );
};

export default MultiSelect;
